import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Document List Component
 * Displays list of documents with status indicators and actions
 */
const DocumentList = ({
  documents,
  isLoading,
  onDocumentSelect,
  selectedDocument,
  onRefresh
}) => {
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: {
        icon: 'CheckCircle',
        text: 'Completed',
        className: 'bg-success/10 text-success border-success/20'
      },
      processing: {
        icon: 'Loader',
        text: 'Processing',
        className: 'bg-warning/10 text-warning border-warning/20'
      },
      pending: {
        icon: 'Clock',
        text: 'Pending',
        className: 'bg-muted text-muted-foreground border-border'
      },
      failed: {
        icon: 'XCircle',
        text: 'Failed',
        className: 'bg-error/10 text-error border-error/20'
      }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${config?.className}`}>
        <Icon 
          name={config?.icon} 
          size={12} 
          className={status === 'processing' ? 'animate-spin' : ''}
        />
        <span>{config?.text}</span>
      </span>
    );
  };

  const getFileTypeIcon = (fileType) => {
    const icons = {
      pdf: 'FileText',
      docx: 'File',
      txt: 'FileText'
    };
    return icons?.[fileType] || 'File';
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="flex items-center justify-center space-x-3">
          <Icon name="Loader" size={24} className="text-primary animate-spin" />
          <p className="text-muted-foreground">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (!documents || documents?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="FileText" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">No documents found</h3>
        <p className="text-muted-foreground mb-4">
          Upload your first document to get started with the AI chatbot
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold text-foreground">Documents</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          className="flex items-center space-x-2"
        >
          <Icon name="RefreshCw" size={16} />
          <span>Refresh</span>
        </Button>
      </div>
      {/* Document List */}
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {documents?.map((doc) => (
          <div
            key={doc?.id}
            onClick={() => onDocumentSelect(doc)}
            className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${
              selectedDocument?.id === doc?.id ? 'bg-primary/5' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                {/* File Icon */}
                <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center shrink-0">
                  <Icon 
                    name={getFileTypeIcon(doc?.file_type)} 
                    size={20} 
                    className="text-primary"
                  />
                </div>

                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate mb-1">
                    {doc?.file_name}
                  </h4>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span className="flex items-center space-x-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDate(doc?.created_at)}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Icon name="HardDrive" size={12} />
                      <span>{formatFileSize(doc?.file_size)}</span>
                    </span>
                    <span className="uppercase font-medium">{doc?.file_type}</span>
                  </div>
                  {doc?.chunk_count > 0 && (
                    <div className="flex items-center space-x-1 text-xs text-muted-foreground mt-1">
                      <Icon name="Database" size={12} />
                      <span>{doc?.chunk_count} chunks created</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Badge */}
              <div className="ml-3">
                {getStatusBadge(doc?.processing_status)}
              </div>
            </div>

            {/* Error Message */}
            {doc?.error_message && (
              <div className="mt-2 ml-13 text-xs text-error">
                Error: {doc?.error_message}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentList;